'use client';

import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ClientSchema } from '@ametista-ai/api-schema';
import * as Sentry from '@sentry/react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { HolidayCountryLabel } from '@/components/Holidays/HolidayCountryLabel';
import { useCreateClient } from '@/queries/clients/useCreateClient';
import { useUpdateClient } from '@/queries/clients/useUpdateClient';
import { useHolidays } from '@/queries/holidays/useHolidays';
import { sortHolidaysByCountryThenDate } from '@/utils/holidayDisplay';
import { DEFAULT_CLIENT_TIMEZONE, TIMEZONE_OPTIONS } from '@/utils/timezones';

interface ClientModalProps {
  client?: ClientSchema;
  onClose: () => void;
}

export const ClientModal = observer(function ClientModal({ client, onClose }: ClientModalProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const { holidays, isLoading, updateHoliday } = useHolidays();
  const sortedHolidays = useMemo(
    () => sortHolidaysByCountryThenDate(holidays, i18n.language, t),
    [holidays, i18n.language, t]
  );

  const [selectedHolidayIds, setSelectedHolidayIds] = useState<string[]>([]);
  const [isSavingHolidays, setIsSavingHolidays] = useState(false);

  const holidaySignature = useMemo(
    () => holidays.map((h) => `${h.id}:${[...h.clientIds].sort().join(',')}`).join('|'),
    [holidays]
  );

  useEffect(() => {
    if (!client) {
      setSelectedHolidayIds([]);
      return;
    }
    if (isLoading) return;
    setSelectedHolidayIds(holidays.filter((h) => h.clientIds.includes(client.id)).map((h) => h.id));
  }, [client, isLoading, holidaySignature, holidays]);

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      name: client?.name ?? '',
      timezone: client?.timezone ?? DEFAULT_CLIENT_TIMEZONE,
    },
    validate: {
      name: (value) => (value.length > 0 ? null : t('client-modal.name-required')),
    },
  });

  const handleToggleHoliday = useCallback((holidayId: string, checked: boolean) => {
    setSelectedHolidayIds((prev) =>
      checked ? [...prev, holidayId] : prev.filter((id) => id !== holidayId)
    );
  }, []);

  const applyHolidaySelectionsToClient = useCallback(
    async (clientId: string) => {
      setIsSavingHolidays(true);
      try {
        for (const holiday of holidays) {
          const shouldApply = selectedHolidayIds.includes(holiday.id);
          const currentlyApplies = holiday.clientIds.includes(clientId);
          if (shouldApply === currentlyApplies) continue;
          const nextClientIds = shouldApply
            ? [...holiday.clientIds, clientId]
            : holiday.clientIds.filter((id) => id !== clientId);
          await updateHoliday.mutateAsync({ id: holiday.id, clientIds: nextClientIds });
        }
      } catch (error) {
        Sentry.captureException(error);
        notifications.show({ message: t('holidays.client.update-failed'), color: 'red' });
        throw error;
      } finally {
        setIsSavingHolidays(false);
      }
    },
    [holidays, selectedHolidayIds, updateHoliday, t]
  );

  const linkSelectedHolidaysToNewClient = useCallback(
    async (clientId: string) => {
      setIsSavingHolidays(true);
      try {
        for (const holidayId of selectedHolidayIds) {
          const holiday = holidays.find((h) => h.id === holidayId);
          if (!holiday) continue;
          if (holiday.clientIds.includes(clientId)) continue;
          await updateHoliday.mutateAsync({
            id: holidayId,
            clientIds: [...holiday.clientIds, clientId],
          });
        }
      } catch (error) {
        Sentry.captureException(error);
        notifications.show({ message: t('holidays.client.update-failed'), color: 'red' });
        throw error;
      } finally {
        setIsSavingHolidays(false);
      }
    },
    [holidays, selectedHolidayIds, updateHoliday, t]
  );

  const handleSubmit = useCallback(
    async (values: typeof form.values) => {
      if (client) {
        await updateClient.mutateAsync({
          id: client.id,
          name: values.name,
          timezone: values.timezone,
        });
        await applyHolidaySelectionsToClient(client.id);
      } else {
        const response = await createClient.mutateAsync({
          name: values.name,
          timezone: values.timezone,
        });
        await linkSelectedHolidaysToNewClient(response.data.client.id);
        navigate(`/client/${response.data.client.id}/settings`);
      }
      onClose();
    },
    [
      form,
      client,
      onClose,
      navigate,
      createClient,
      updateClient,
      applyHolidaySelectionsToClient,
      linkSelectedHolidaysToNewClient,
    ]
  );

  const isSubmitDisabled =
    createClient.isPending ||
    updateClient.isPending ||
    !form.isValid() ||
    updateHoliday.isPending ||
    isSavingHolidays ||
    (client !== undefined && isLoading);

  const isSubmitLoading = createClient.isPending || updateClient.isPending || isSavingHolidays;

  return (
    <Modal
      opened
      onClose={onClose}
      title={client ? t('client-modal.edit-client') : t('client-modal.add-client')}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            data-autofocus
            label={t('client-modal.name')}
            {...form.getInputProps('name')}
          />

          <Select
            label={t('clients.timezone')}
            searchable
            allowDeselect={false}
            data={TIMEZONE_OPTIONS}
            {...form.getInputProps('timezone')}
          />

          <Stack gap="xs">
            <Text size="sm" fw={500}>
              {t('client-modal.holidays')}
            </Text>
            {isLoading && <Loader size="sm" />}
            {!isLoading && holidays.length === 0 && (
              <Text size="sm" c="dimmed">
                {t('holidays.client.no-agency-holidays')}
              </Text>
            )}
            {!isLoading && holidays.length > 0 && (
              <ScrollArea mah={280}>
                <Table highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('holidays.name')}</Table.Th>
                      <Table.Th>{t('holidays.country')}</Table.Th>
                      <Table.Th>{t('holidays.date')}</Table.Th>
                      <Table.Th ta="right">{t('holidays.client.applies')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {sortedHolidays.map((holiday) => {
                      const isChecked = selectedHolidayIds.includes(holiday.id);
                      return (
                        <Table.Tr key={holiday.id}>
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {holiday.name}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <HolidayCountryLabel country={holiday.country} />
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              {dayjs(holiday.date).format('MMM D, YYYY')}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Group justify="flex-end">
                              <Checkbox
                                checked={isChecked}
                                onChange={(e) =>
                                  handleToggleHoliday(holiday.id, e.currentTarget.checked)
                                }
                                disabled={updateHoliday.isPending || isSavingHolidays}
                                aria-label={t('holidays.client.applies')}
                              />
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}
          </Stack>

          <Group justify="flex-end">
            <Button type="submit" disabled={isSubmitDisabled} loading={isSubmitLoading}>
              {client ? t('client-modal.update') : t('client-modal.add')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
});
